import type { DocMeta } from "./types";

const SLUG = "privacy-policy";
const id = (s: string) => `${SLUG}-${s}`;

export const PRIVACY_POLICY_META: DocMeta = {
  slug: SLUG,
  title: "Privacy Policy",
  subtitle: "How Logical Health handles privacy, consent, and data protection",
  description:
    "How Logical Health collects, uses, protects, and shares information through the public website and direct interactions.",
  lastUpdated: "May 31, 2026",
  sections: [
    { id: id("introduction"), title: "Introduction" },
    { id: id("scope-and-coverage"), title: "1. Scope and Coverage" },
    {
      id: id("information-we-collect"),
      title: "2. Information We Collect",
    },
    {
      id: id("cookies-and-similar-technologies"),
      title: "3. Cookies and Similar Technologies",
    },
    { id: id("messaging-data"), title: "4. Messaging Data" },
    { id: id("how-we-use-information"), title: "5. How We Use Information" },
    {
      id: id("how-we-share-information"),
      title: "6. How We Share Information",
    },
    {
      id: id("privacy-requests-and-your-choices"),
      title: "7. Privacy Requests and Your Choices",
    },
    {
      id: id("data-protection-and-security"),
      title: "8. Data Protection and Security",
    },
    { id: id("retention"), title: "9. Retention" },
    { id: id("children"), title: "10. Children" },
    {
      id: id("state-and-international-privacy-rights"),
      title: "11. State and International Privacy Rights",
    },
    {
      id: id("changes-to-this-privacy-policy"),
      title: "12. Changes to This Privacy Policy",
    },
    { id: id("contact-information"), title: "13. Contact Information" },
  ],
};

export function PrivacyPolicy() {
  return (
    <>
      <section id={id("introduction")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">Introduction</h2>
        <p>
          This Privacy Policy describes how Logical Health, Inc.
          (&ldquo;Logical Health&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;,
          and &ldquo;our&rdquo;) collects, uses, protects, and shares
          information when you visit{" "}
          <strong>https://logical.health</strong>, submit a form, request a
          demo, opt in to text messaging, or otherwise interact with our
          public website and related services.
        </p>
        <p>
          Logical Health provides healthcare technology and member-support
          services. For many customer deployments, Logical Health acts as a
          HIPAA Business Associate to Covered Entity clients. In that role,
          our handling of protected health information (&ldquo;PHI&rdquo;)
          and electronic protected health information (&ldquo;ePHI&rdquo;) is
          governed by Business Associate Agreements, client instructions,
          HIPAA/HITECH requirements, and our internal compliance controls.
        </p>
        <p>
          Logical Health does not sell, rent, buy, or share SMS/RCS opt-in
          consent data with third parties or affiliates for their own marketing
          or promotional purposes.
        </p>
        <p>
          No mobile information will be shared with third party/affiliates for
          marketing or promotional purposes.
        </p>
        <p>
          This policy explains our public website and user-facing privacy
          practices. It does not replace any Business Associate Agreement,
          customer contract, client privacy notice, or legally required
          health plan notice that may apply to PHI handled on behalf of a
          Covered Entity client.
        </p>
      </section>

      <section id={id("scope-and-coverage")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          1. Scope and Coverage
        </h2>
        <p>This Privacy Policy applies to information we collect:</p>
        <ul className="ml-6 space-y-2">
          <li>On the Logical Health website.</li>
          <li>
            Through contact, demo, newsletter, or similar website forms.
          </li>
          <li>
            Through our SMS/RCS opt-in flow and related consent records.
          </li>
          <li>
            Through email, text, and other electronic communications with
            Logical Health.
          </li>
          <li>
            Through analytics and similar technologies used to understand
            website performance and improve user experience.
          </li>
        </ul>
        <p className="mt-4">
          This Privacy Policy does not apply to third-party websites,
          services, or applications that we do not control, even if they link
          to or are linked from our website.
        </p>
      </section>

      <section id={id("information-we-collect")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          2. Information We Collect
        </h2>
        <p>
          We collect information only as needed for website operation,
          communications, customer support, service delivery, compliance,
          security, and other legitimate business purposes.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Information You Provide
        </h3>
        <ul className="ml-4 space-y-2">
          <li>
            Contact and demo request details, such as name, business or
            organization, email address, phone number, and message content.
          </li>
          <li>
            Scheduling details when you book or request a meeting with us.
          </li>
          <li>
            Correspondence and support information you send to us by email,
            text, web form, or other communication channels.
          </li>
          <li>
            SMS/RCS consent information, including phone number, opt-in
            method, disclosure version, consent status, and related
            timestamps or confirmation records.
          </li>
        </ul>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          Information Collected Automatically
        </h3>
        <p>
          When you visit our website, we may collect device, browser, and
          usage information, including pages visited, referring pages,
          browser type, device type, approximate location inferred from IP
          address, interaction events, and other technical information used
          to operate, secure, and improve the site.
        </p>
        <p className="mt-4">
          Where enabled, our website may use Google Analytics and PostHog
          for site and product analytics. We may also use service providers
          such as Calendly for scheduling and EmailJS for contact-form
          delivery.
        </p>

        <h3 className="text-2xl font-semibold text-gray-800 mb-4 mt-8">
          PHI and Client-Controlled Data
        </h3>
        <p>
          When Logical Health processes PHI/ePHI on behalf of a Covered
          Entity client, that information is handled under the applicable
          Business Associate Agreement, client instructions, HIPAA/HITECH,
          and Logical Health compliance requirements. We apply
          minimum-necessary and data-minimization principles to limit
          collection, access, use, disclosure, logging, retention, and
          sharing of sensitive information.
        </p>
      </section>

      <section id={id("cookies-and-similar-technologies")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          3. Cookies and Similar Technologies
        </h2>
        <p>
          We use cookies and similar technologies for website operation,
          analytics, performance measurement, and user-experience improvement.
          These technologies may help us understand how users find and use
          our website, diagnose errors, measure campaign effectiveness, and
          improve content.
        </p>
        <p className="mt-4">
          You can control cookies through your browser settings and other
          tools made available by analytics providers. For more detail about
          the types of cookies and similar technologies we use, please see
          our{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>
          .
        </p>
      </section>

      <section id={id("messaging-data")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          4. Messaging Data
        </h2>
        <p>
          If you opt in to receive SMS, RCS, WhatsApp, or similar messages
          from Logical Health, we may collect and use your phone number or
          messaging handle, opt-in method, consent status, disclosure
          version, timestamps, HELP/STOP requests, message metadata, and
          related records to operate, secure, document, and support the
          messaging program.
        </p>
        <p className="mt-4">
          The full operational terms (opt-in flow, HELP/STOP behavior,
          carrier disclaimer, third-party sharing scope) live on our{" "}
          <a
            href="/messaging-terms"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Messaging Terms
          </a>{" "}
          page. Messaging content and PHI/ePHI, if any, may be governed by
          the applicable customer agreement, Business Associate Agreement,
          customer instructions, and our Messaging Terms.
        </p>
      </section>

      <section id={id("how-we-use-information")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          5. How We Use Information
        </h2>
        <p>
          We use information we collect or receive for the following
          purposes:
        </p>
        <ul className="ml-6 space-y-2">
          <li>To operate, maintain, secure, and improve our website.</li>
          <li>
            To respond to inquiries, demo requests, support requests, and
            other communications.
          </li>
          <li>
            To provide requested services, information, scheduling, and
            customer or member support.
          </li>
          <li>
            To administer SMS/RCS consent, confirmations, opt-outs, HELP
            requests, and related messaging operations.
          </li>
          <li>
            To analyze website usage, diagnose issues, and improve product
            and site experience.
          </li>
          <li>
            To comply with legal, contractual, regulatory, security, audit,
            and compliance obligations.
          </li>
          <li>
            To protect the rights, safety, security, and integrity of
            Logical Health, our customers, users, systems, and services.
          </li>
        </ul>
      </section>

      <section id={id("how-we-share-information")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          6. How We Share Information
        </h2>
        <p>
          We do not sell personal information. We may share information only
          as needed for the purposes described in this policy, including:
        </p>
        <ul className="ml-6 space-y-2">
          <li>
            With service providers and approved vendors that help operate our
            website, analytics, scheduling, communications, hosting, security,
            and business operations.
          </li>
          <li>
            With Covered Entity clients, business partners, or authorized
            stakeholders when needed to provide contracted services or route
            client-controlled requests.
          </li>
          <li>
            With contractors or advisors who need access for legitimate
            business purposes and are subject to confidentiality, contractual,
            or professional obligations.
          </li>
          <li>
            When required by law, legal process, government request,
            regulation, subpoena, or court order.
          </li>
          <li>
            In connection with a merger, acquisition, financing,
            reorganization, or sale of assets, subject to appropriate
            confidentiality and legal protections.
          </li>
          <li>With your consent or at your direction.</li>
        </ul>
        <p className="mt-4">
          Vendors that process sensitive data are reviewed under Logical
          Health vendor-management requirements and, where appropriate,
          covered by Business Associate Agreements, data protection
          agreements, contractual safeguards, or assurance evidence.
        </p>
        <p className="mt-4">
          Text Messaging Consent Data. All categories of sharing described
          above exclude text messaging originator opt-in data and consent.
          Logical Health does not sell, rent, buy, or share SMS/RCS opt-in
          consent data with third parties or affiliates for their own
          marketing or promotional purposes. Text messaging consent data is
          used only to operate, secure, support, document, and comply with the
          Logical Health messaging program, including disclosures to messaging
          service providers where necessary to deliver and administer the
          messages.
        </p>
      </section>

      <section id={id("privacy-requests-and-your-choices")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          7. Privacy Requests and Your Choices
        </h2>
        <p>
          You may contact us at{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>{" "}
          to request access to, correction of, or deletion of personal
          information that you provided directly to Logical Health, subject
          to applicable law, identity verification, contractual obligations,
          and retention requirements.
        </p>
        <p className="mt-4">
          Because Logical Health primarily acts as a HIPAA Business Associate
          for PHI/ePHI handled on behalf of Covered Entity clients, privacy
          requests involving client-controlled health information are routed
          through the applicable Covered Entity client by default unless
          direct handling has been contractually delegated to Logical Health.
        </p>
        <p className="mt-4">
          You may opt out of SMS/RCS messaging by replying STOP. You may use
          browser settings and provider tools to limit cookies and analytics
          technologies as described in our{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>
          .
        </p>
      </section>

      <section id={id("data-protection-and-security")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          8. Data Protection and Security
        </h2>
        <p>
          Logical Health maintains administrative, technical, and
          organizational safeguards designed to protect personal information,
          PHI/ePHI, client data, and other sensitive information. These
          safeguards include access control, least privilege, multi-factor
          authentication where appropriate, encryption in transit and at rest
          where supported, logging, monitoring, vendor review, workforce
          training, incident response, and privacy-by-design practices.
        </p>
        <p className="mt-4">
          No website, network, or transmission method is completely secure.
          We maintain safeguards appropriate to the nature of the data and
          our role, but we cannot guarantee absolute security.
        </p>
      </section>

      <section id={id("retention")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">9. Retention</h2>
        <p>
          We retain information for as long as reasonably necessary to provide
          services, respond to requests, maintain consent records, operate
          and secure our systems, comply with legal or contractual
          obligations, resolve disputes, preserve audit evidence, and enforce
          agreements.
        </p>
        <p className="mt-4">
          Compliance, security, audit, incident, vendor, access-review,
          privacy-request, and PHI/ePHI-related records are retained in
          accordance with applicable legal, contractual, HIPAA/HITECH, and
          internal retention requirements. Transient operational telemetry
          and temporary artifacts may follow risk-based platform retention
          unless they are preserved as compliance, audit, incident, legal,
          or contractual evidence.
        </p>
      </section>

      <section id={id("children")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">10. Children</h2>
        <p>
          Our website and services are not intended for children under 18
          years of age. We do not knowingly collect personal information from
          children under 18 through the website. If you believe a child has
          provided personal information to us, please contact us at{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          .
        </p>
      </section>

      <section
        id={id("state-and-international-privacy-rights")}
        className="mb-12"
      >
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          11. State and International Privacy Rights
        </h2>
        <p>
          Depending on where you live and the context in which we process
          your information, you may have rights to request access,
          correction, deletion, restriction, portability, objection, or
          information about certain disclosures. You may submit a request at{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          .
        </p>
        <p className="mt-4">
          We do not currently sell personal information in a way that
          triggers Nevada opt-out requirements. California residents may
          contact us to request information about certain disclosures for
          direct marketing purposes.
        </p>
      </section>

      <section id={id("changes-to-this-privacy-policy")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          12. Changes to This Privacy Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time to reflect
          changes in our practices, technologies, legal requirements, or
          services. When we make changes, we will post the updated policy on
          this page and update the &ldquo;Last Updated&rdquo; date.
        </p>
      </section>

      <section id={id("contact-information")} className="mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-6">
          13. Contact Information
        </h2>
        <p>
          For questions about this Privacy Policy or our privacy practices,
          contact us at{" "}
          <a
            href="mailto:compliance@logical.health"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            compliance@logical.health
          </a>
          .
        </p>
        <p className="mt-4">
          You may also review our{" "}
          <a
            href="/legal#terms-of-service"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="/cookie-policy"
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            Cookie Policy
          </a>
          .
        </p>
      </section>
    </>
  );
}
